<template>
  <div class="anz-position-relative">
    <textarea
      v-model="input"
      :placeholder="placeholder"
      class="anz-fc-field"
      :rows="1"
      @input="handleInput"
      @focusin="onFocusIn"
      data-cy="post-create-input-text"
      ref="postContentRef"
    >
    </textarea>
    <div class="anz-posts__item-create-bottom">
      <div class=" anz-flex anz-flex-wrap anz-justify-content-between">
        <div
          class="anz-posts__item-create-letters anz-color-black-60 anz-fs-12 anz-lh-165 anz-pr-s anz-flex anz-flex-wrap anz-color-gray-700"
        >
          <post-text-counter :input="input" />
        </div>
        <div class="anz-fc-emoji-wrapper anz-fc-icon anz-hidden-mo">
          <a-button
            className="anz-link anz-color-black-35"
            :class="{ active: showEmojis }"
            @click.stop="showEmojis = !showEmojis"
          >
            <template #title>
              <a-icon
                v-if="!showEmojis"
                name="emoji-stroke"
                width="1.285em"
                height="1.285em"
                class="anz-block"
                stroke
              />
              <a-icon
                v-else
                name="emoji"
                width="1.285em"
                height="1.285em"
                class="anz-block"
                stroke
              />
            </template>
          </a-button>
        </div>
      </div>
      <div
        class="anz-fc-emoji anz-hidden-mo"
        :class="{ 'is-active': showEmojis }"
        ref="emojiContentRef"
      >
        <emoji-picker v-if="!savingPost" @insert-emoji="insertEmoji">
        </emoji-picker>
      </div>
    </div>
  </div>

  <template v-if="hasError">
    <div
      v-for="message in errorMessages"
      :key="message"
      class="anz-fc-alert anz-fc-alert-error anz-px-l anz-py-s anz-mb-s"
    >
      <span class="anz-fc-alert-msg">{{ $translate(message) }}</span>
    </div>
  </template>
</template>

<script lang="ts">
import { defineComponent, Ref, nextTick } from 'vue'
import { ref } from 'vue'
import AButton from '@/components/Forms/AButton.vue'
import AIcon from '@/components/AIcon.vue'
import EmojiPicker from '@/components/Forms/EmojiPicker.vue'
import { PostContentConstraints } from '@/validation/postValidator'
import PostTextCounter from '@/components/Forms/PostTextCounter.vue'
import useEmojiShortcuts from '@/composables/emojis/emojiShortcuts'

export default defineComponent({
  name: 'PostText',
  components: { PostTextCounter, EmojiPicker, AIcon, AButton },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    className: {
      type: String,
      default: '',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    savingPost: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: [],
    },
  },
  data() {
    return {
      input: '',
      CONTENT_MAX_LENGTH: PostContentConstraints.MAX_LENGTH,
      lastPos: 0,
      showEmojis: false,
    }
  },
  watch: {
    modelValue() {
      this.input = this.modelValue
    },
  },
  methods: {
    handleInput(lastChar: InputEvent) {
      this.resize()
      this.lastPos = 0
      let cursorStart: number | undefined

      const textarea: any = this.$refs.postContentRef
      if (textarea !== 'undefined') {
        cursorStart = textarea.selectionStart - 1

        this.input = this.replaceEmoji(
          lastChar.data ?? '',
          cursorStart,
          this.input
        )
      }

      this.$emit('update:modelValue', this.input)
      nextTick(() => {
        if (cursorStart) {
          textarea.setSelectionRange(cursorStart + 1, cursorStart + 1)
        }
      })
    },
    onFocusIn() {
      this.lastPos = 0
    },
    resize() {
      const textareaEl: any = this.$refs.postContentRef

      if (typeof textareaEl === 'undefined') {
        return false
      }

      textareaEl.style.height = 'auto'
      textareaEl.style.height = `${textareaEl.scrollHeight}px`
    },
    insertEmoji(emoji: any) {
      if (typeof this.input !== 'undefined') {
        const textarea: any = this.$refs.postContentRef

        if (typeof textarea === 'undefined') {
          return false
        }

        let cursorStart: number = textarea.selectionStart
        let cursorEnd: number = textarea.selectionEnd

        if (this.lastPos > 0) {
          cursorStart = cursorEnd = this.lastPos
        }

        const beforeText = this.input.slice(0, cursorStart)
        const afterText = this.input.slice(cursorEnd)

        this.input = beforeText + emoji + afterText
        textarea.setSelectionRange(beforeText, beforeText)

        this.lastPos = cursorStart + 2
        this.$emit('update:modelValue', this.input)
      }
    },
  },
  mounted() {
    this.input = this.modelValue
  },
  updated() {
    this.resize()
  },
  setup() {
    const emojiContentRef: Ref<Node | undefined> = ref()
    const postContentRef: Ref<Node | undefined> = ref()

    return {
      postContentRef,
      emojiContentRef,
      ...useEmojiShortcuts(),
    }
  },
})
</script>
